import React, { useState, useEffect } from 'react';
import useUserSubscription from '../hooks/useUserSubscription';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getPlayers,
  getFamilyLeaderboard,
  getCompletionCount,
  verifyParentPin,
  createUserProfile,
  updatePlayerDetails,
} from '../firebase';
import Button from './Button';
import PointBankModal from './PointBankModal';
import BadgeDisplay from './BadgeDisplay';
import PlayerAvatar from './PlayerAvatar';
import LeaderboardTable from './LeaderboardTable';
import AchievementToast from './AchievementToast';
import { getCurrentLevel, getAllAchievements } from '../utils/achievements';
// import { animalEmojis } from '../utils/constants';
import { createPlayer } from '../firebase';
import SimpleModal from './SimpleModal';
import Input from './Input';
import PayPalSubscribeButton from './PayPalSubscribeButton';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import './Dashboard.css';
import { KiddiFitWordmark } from './Brand';
import PlayerModal from './PlayerModal';
import AddToHomeTodo from './AddToHomeTodo';

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPremium, premiumEndsAt } = useUserSubscription();
  const [players, setPlayers] = useState([]);
  const [familyLeaderboard, setFamilyLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showPointBank, setShowPointBank] = useState(false);
  const [achievementToast, setAchievementToast] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [deletePin, setDeletePin] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [isVerifyingPin, setIsVerifyingPin] = useState(false);
  const [isDeletePinValid, setIsDeletePinValid] = useState(false);
  const [showCancelPremium, setShowCancelPremium] = useState(false);
  const [isCancellingPremium, setIsCancellingPremium] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [activated, setActivated] = useState(false);
  const [showResetPin, setShowResetPin] = useState(false);
  const [newParentPin, setNewParentPin] = useState('');
  const [confirmParentPin, setConfirmParentPin] = useState('');
  const [pinUpdateError, setPinUpdateError] = useState('');
  const [isUpdatingPin, setIsUpdatingPin] = useState(false);
  const [showEditPlayer, setShowEditPlayer] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [isUpdatingPlayer, setIsUpdatingPlayer] = useState(false);

  useEffect(() => {
    if (isActivating && isPremium) {
      setActivated(true);
    }
  }, [isActivating, isPremium]);

  useEffect(() => {
    if (user) {
      loadPlayers();
      loadFamilyLeaderboard();
    }
  }, [user]);

  // Check if user has no players and redirect to onboarding
  useEffect(() => {
    if (!loading && players.length === 0) {
      navigate('/onboarding');
    }
  }, [loading, players.length, navigate]);

  // Check for streak achievements on app load
  useEffect(() => {
    if (players.length > 0) {
      checkStreakAchievements();
    }
  }, [players]);

  const checkStreakAchievements = () => {
    players.forEach((player) => {
      if (player.streakDays > 0) {
        const achievements = getAllAchievements(player, player.totalScore || 0);
        if (achievements.newStreakMilestones.length > 0) {
          setAchievementToast({
            achievement: achievements.newStreakMilestones[0],
            playerName: player.playerName,
          });
          return; // Show first achievement only
        }
      }
    });
  };

  const loadPlayers = async () => {
    try {
      const playersData = await getPlayers(user.uid);
      setPlayers(playersData);
    } catch (error) {
      console.error('Error loading players:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFamilyLeaderboard = async () => {
    try {
      const leaderboardData = await getFamilyLeaderboard(user.uid);
      setFamilyLeaderboard(leaderboardData);
    } catch (error) {
      console.error('Error loading family leaderboard:', error);
    }
  };

  const handleStartWorkout = () => {
    navigate('/workouts/players');
  };
  const handleStartSkills = () => {
    navigate('/skills/players');
  };

  const handleViewLeaderboard = () => {
    navigate('/leaderboard');
  };

  const handleAddPlayer = () => {
    setShowCreateForm(true);
  };

  const handleLogout = async () => {
    navigate('/logout');
  };

  const handlePointBank = (player) => {
    setSelectedPlayer(player);
    setShowPointBank(true);
  };

  const handleClosePointBank = () => {
    setShowPointBank(false);
    setSelectedPlayer(null);
  };

  const handlePointsUpdated = () => {
    loadPlayers(); // Reload players to get updated point balances
  };

  const handleCloseAchievementToast = () => {
    setAchievementToast(null);
  };

  const submitCreatePlayer = async ({ name, age, emoji }) => {
    setCreating(true);
    try {
      await createPlayer(user.uid, name, emoji, age);
      setShowCreateForm(false);
      await loadPlayers();
      await loadFamilyLeaderboard();
    } catch (error) {
      console.error('Error creating player:', error);
      alert('Failed to create player. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenDelete = (row) => {
    // Resolve the actual player object so we have the Firestore doc id
    const resolvedPlayer = players.find(
      (p) => p.playerId === row.id || p.playerName === row.name
    );

    if (!resolvedPlayer) {
      setDeleteError('Could not find player to delete.');
      return;
    }

    setPlayerToDelete(resolvedPlayer);
    setDeletePin('');
    setDeleteError('');
    setIsDeletePinValid(false);
    setIsVerifyingPin(false);
    setShowDeleteModal(true);
  };

  // Verify delete PIN against Firebase when 4 digits are entered
  useEffect(() => {
    let timeoutId;
    let cancelled = false;
    setDeleteError('');

    if (deletePin && deletePin.length === 4 && user) {
      setIsVerifyingPin(true);
      timeoutId = setTimeout(async () => {
        try {
          const valid = await verifyParentPin(user.uid, deletePin);
          if (!cancelled) {
            setIsDeletePinValid(Boolean(valid));
            if (!valid) {
              setDeleteError('Incorrect PIN');
            }
          }
        } catch (err) {
          if (!cancelled) {
            setIsDeletePinValid(false);
            setDeleteError('Could not verify PIN');
          }
        } finally {
          if (!cancelled) setIsVerifyingPin(false);
        }
      }, 250);
    } else {
      setIsDeletePinValid(false);
      setIsVerifyingPin(false);
    }

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [deletePin, user]);

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      // reuse existing PlayerManagement flow via firebase util
      const { deletePlayer } = await import('../firebase');
      await deletePlayer(user.uid, playerToDelete.id, deletePin);
      setShowDeleteModal(false);
      await loadPlayers();
      await loadFamilyLeaderboard();
    } catch (e) {
      console.error('Delete failed', e);
      setDeleteError('Invalid PIN or delete failed.');
    } finally {
      setIsDeleting(false);
    }
  };

  const getFamilyStats = () => {
    const totalPoints = players.reduce(
      (sum, player) => sum + (player.totalScore || 0),
      0
    );
    const totalWorkouts = players.reduce(
      (sum, player) => sum + getCompletionCount(player, 'workout'),
      0
    );
    const totalSkills = players.reduce(
      (sum, player) => sum + getCompletionCount(player, 'skill'),
      0
    );
    const activeStreaks = players.filter(
      (player) => (player.streak || 0) > 0
    ).length;

    return { totalPoints, totalWorkouts, totalSkills, activeStreaks };
  };

  if (loading) {
    return (
      <div className='dashboard'>
        <div className='loading'>Loading dashboard...</div>
      </div>
    );
  }

  const familyStats = getFamilyStats();

  return (
    <div className='dashboard leaderboard-page'>
      <div className='dashboard-header'>
        <div className='header-content'>
          <div>
            <h1>
              <KiddiFitWordmark />
            </h1>
            <div style={{ marginBottom: 8 }}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  borderRadius: 999,
                  fontSize: 12,
                  lineHeight: 1,
                  fontWeight: 700,
                  background: isPremium
                    ? 'rgba(255, 20, 147, 0.15)'
                    : 'rgba(255,255,255,0.12)',
                  color: isPremium ? '#ff1493' : '#bbb',
                  border: isPremium
                    ? '1px solid rgba(255, 20, 147, 0.4)'
                    : '1px solid rgba(255,255,255,0.25)',
                  cursor: isPremium ? 'default' : 'pointer',
                }}
                onClick={() => {
                  if (!isPremium) setShowUpgrade(true);
                }}
                title={isPremium ? 'Premium Member' : 'Upgrade to Premium'}
              >
                {isPremium ? 'Premium Member' : 'Basic Member'}
              </span>
            </div>
            {/* <p className='subtitle'>
              Welcome back! Here's how your players are doing.
            </p> */}
          </div>
        </div>
      </div>

      {/* Focus */}
      <div className='dashboard-section'>
        <h2>What do you want to focus on today?</h2>
        <div className='quick-actions'>
          <Button type='primary' onClick={handleStartWorkout}>
            Build Strength
          </Button>
          <Button type='primary' onClick={handleStartSkills}>
            Work on Skills
          </Button>
        </div>
      </div>

      {/* Family Stats Overview */}
      <div className='family-stats'>
        <div className='stat-card'>
          <div className='stat-icon'>🏆</div>
          <div className='stat-content'>
            <div className='stat-value'>{familyStats.totalPoints}</div>
            <div className='stat-label'>Total Points</div>
          </div>
        </div>
        <div className='stat-card'>
          <div className='stat-icon'>💪</div>
          <div className='stat-content'>
            <div className='stat-value'>{familyStats.totalWorkouts}</div>
            <div className='stat-label'>Strength Workouts</div>
          </div>
        </div>
        <div className='stat-card'>
          <div className='stat-icon'>🎯</div>
          <div className='stat-content'>
            <div className='stat-value'>{familyStats.totalSkills}</div>
            <div className='stat-label'>Skill Sessions</div>
          </div>
        </div>
        <div className='stat-card'>
          <div className='stat-icon'>🔥</div>
          <div className='stat-content'>
            <div className='stat-value'>{familyStats.activeStreaks}</div>
            <div className='stat-label'>Active Streaks</div>
          </div>
        </div>
      </div>

      {/* Add to Home (mobile only, self-hiding) */}
      <AddToHomeTodo />

      {/* Family Leaderboard */}
      <div className='dashboard-section leaderboard-section'>
        <h2>Family Leaderboard</h2>
        <LeaderboardTable
          showActions
          onBankClick={(row) => {
            const playerData = players.find(
              (p) => p.playerId === row.id || p.playerName === row.name
            );
            if (playerData) handlePointBank(playerData);
          }}
          onEditClick={(row) => {
            const playerData = players.find(
              (p) => p.playerId === row.id || p.playerName === row.name
            );
            if (playerData) {
              setEditingPlayer(playerData);
              setShowEditPlayer(true);
            }
          }}
          onDeleteClick={handleOpenDelete}
          onNameClick={(row) => navigate(`/profile/${row.id}`)}
          rows={familyLeaderboard.map((player) => {
            const levelInfo = getCurrentLevel(player.totalPoints || 0);
            return {
              id: player.playerId,
              rank: player.rank,
              avatar: player.avatar || '🐶',
              name:
                player.displayName !== player.playerId
                  ? `${player.displayName} (${player.playerId})`
                  : player.displayName,
              points: player.totalPoints || 0,
              levelNumber: levelInfo.level,
              levelEmoji: levelInfo.emoji,
            };
          })}
        />
      </div>

      {/* Badges Section - compact rows */}
      <div className='dashboard-section' style={{ padding: '20px 0 0' }}>
        <h2>Player Badges</h2>
        <div className='badges-container'>
          {players.map((player) => {
            const levelInfo = getCurrentLevel(player.totalScore || 0);
            const badges = player.badges || [];
            return (
              <div key={player.id} className='badge-row'>
                <PlayerAvatar
                  emoji={player.avatar || '🐶'}
                  name={player.playerName}
                  levelNumber={levelInfo.level}
                  levelEmoji={levelInfo.emoji}
                  size={32}
                />
                <div className='badge-strip'>
                  {badges.length > 0 ? (
                    badges.map((b, i) => (
                      <span
                        className='badge-chip'
                        key={i}
                        title={b?.name || ''}
                      >
                        {b?.emoji || '⭐'}
                      </span>
                    ))
                  ) : (
                    <span className='stat-label'>No badges yet</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className='dashboard-section'>
        <h2>Quick Actions</h2>
        <div className='quick-actions'>
          <Button type='secondary' onClick={handleAddPlayer}>
            Add Player
          </Button>
          <Button type='secondary' onClick={() => setShowResetPin(true)}>
            Reset PIN
          </Button>
          <Button type='danger' onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </div>

      {/* Membership Section */}
      {isPremium && (
        <div className='dashboard-section'>
          <h2>Your Membership Plan</h2>
          <div
            className='stat-card'
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div
              className='stat-content'
              style={{
                textAlign: 'left',
                margin: 'auto',
              }}
            >
              <div className='stat-value'>
                <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>
                  $19.99
                </span>{' '}
                $9.99/mo
              </div>
              <div className='stat-label'>
                Thank you for being a premium member. We appreciate your
                support!
              </div>
              {premiumEndsAt && (
                <div
                  className='stat-label'
                  style={{ marginTop: 6, opacity: 0.9 }}
                >
                  {`Access through ${new Date(
                    premiumEndsAt
                  ).toLocaleDateString()}`}
                </div>
              )}
              <br />
              <Button
                type='secondary'
                size='small'
                onClick={() => setShowCancelPremium(true)}
              >
                Cancel Premium
              </Button>
            </div>
          </div>
        </div>
      )}
      <br />
      <br />
      <br />

      {/* Point Bank Modal */}
      {showPointBank && selectedPlayer && (
        <PointBankModal
          player={selectedPlayer}
          onClose={handleClosePointBank}
          onPointsUpdated={handlePointsUpdated}
        />
      )}

      {/* Achievement Toast */}
      {achievementToast && (
        <AchievementToast
          achievement={achievementToast.achievement}
          playerName={achievementToast.playerName}
          onClose={handleCloseAchievementToast}
          autoDismissTime={10000}
        />
      )}

      <PlayerModal
        isOpen={showCreateForm}
        mode='create'
        isSubmitting={creating}
        onClose={() => setShowCreateForm(false)}
        onSubmit={submitCreatePlayer}
      />

      {/* Edit Player Modal */}
      <PlayerModal
        isOpen={showEditPlayer}
        mode='edit'
        isSubmitting={isUpdatingPlayer}
        initialName={editingPlayer?.playerName || ''}
        initialAge={
          typeof editingPlayer?.age === 'number' ? editingPlayer.age : ''
        }
        initialEmoji={editingPlayer?.avatar || '🐶'}
        onClose={() => setShowEditPlayer(false)}
        onSubmit={async ({ name, age, emoji }) => {
          if (!editingPlayer) return;
          try {
            setIsUpdatingPlayer(true);
            await updatePlayerDetails(
              user.uid,
              editingPlayer.id,
              name,
              emoji,
              age
            );
            setShowEditPlayer(false);
            await loadPlayers();
            await loadFamilyLeaderboard();
          } catch (e) {
            console.error('Update player failed', e);
          } finally {
            setIsUpdatingPlayer(false);
          }
        }}
      />

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
      />

      {/* Cancel Premium Modal */}
      <SimpleModal
        isOpen={showCancelPremium}
        onClose={() => setShowCancelPremium(false)}
        title='Are you sure?'
        body={
          <>
            <h3>Cancel Premium Membership</h3>
            <p>
              Thank you for being a premium member. We hope that you've enjoyed
              our workouts and skill sessions. We will miss you!
            </p>
            <p>
              Please note that by cancelling you will lose access to Premium
              features, including the complete <KiddiFitWordmark /> library.
            </p>
          </>
        }
        actions={[
          {
            text: 'Keep Premium',
            type: 'secondary',
            onClick: () => setShowCancelPremium(false),
            disabled: isCancellingPremium,
          },
          {
            text: isCancellingPremium ? 'Cancelling…' : 'Confirm',
            type: 'danger',
            onClick: async () => {
              try {
                setIsCancellingPremium(true);
                await fetch('/paypal/cancel', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ uid: user.uid }),
                });
                setShowCancelPremium(false);
              } catch (e) {
                console.error('Cancel premium failed', e);
              } finally {
                setIsCancellingPremium(false);
              }
            },
            disabled: isCancellingPremium,
          },
        ]}
      />

      <SimpleModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title='Delete Player'
        type='danger'
        message={`This will permanently remove all stats, points, streaks, and history for ${
          playerToDelete?.playerName || ''
        }. This action cannot be undone.`}
        actions={[
          {
            text: 'Cancel',
            type: 'secondary',
            onClick: () => setShowDeleteModal(false),
          },
          {
            text: isDeleting ? 'Deleting...' : 'Confirm Delete',
            type: 'danger',
            onClick: handleConfirmDelete,
            disabled:
              isDeleting ||
              deletePin.length !== 4 ||
              isVerifyingPin ||
              !isDeletePinValid,
          },
        ]}
      >
        <div className='pin-input-section'>
          <label>Enter Parent PIN:</label>
          <Input
            type='tel'
            value={deletePin}
            onChange={(e) =>
              setDeletePin(e.target.value.replace(/\D/g, '').slice(0, 4))
            }
            placeholder='0000'
            maxLength={4}
            pattern='[0-9]*'
            inputMode='numeric'
            autoComplete='off'
          />
          {deleteError && (
            <div
              className='error-message'
              style={{ color: '#ff7675', marginTop: 6 }}
            >
              {deleteError}
            </div>
          )}
        </div>
      </SimpleModal>

      {/* Reset PIN Modal */}
      <SimpleModal
        isOpen={showResetPin}
        onClose={() => setShowResetPin(false)}
        title='Reset PIN'
        body={
          <>
            <p>
              Email us at <strong>info@kiddifit.com</strong> and we will send
              you a new PIN.
            </p>
            <div style={{ marginTop: 20 }}>
              <Button
                size='small'
                type='primary'
                onClick={() => {
                  const to = 'info@kiddifit.com';
                  const subject = 'Reset PIN';
                  const body = `user id: ${user?.uid || ''}\nemail: ${
                    user?.email || ''
                  }`;
                  const href = `mailto:${to}?subject=${encodeURIComponent(
                    subject
                  )}&body=${encodeURIComponent(body)}`;
                  window.location.href = href;
                }}
              >
                Reset PIN
              </Button>
              <br />
              <br />
              <br />
              <br />
            </div>
          </>
        }
      />
    </div>
  );
}

export default Dashboard;
